import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useUserAuth } from "@/context/UserAuthContext";
import { toast } from "sonner";

/**
 * One-shot handler for the Emergent OAuth callback. Reads session_id from the URL fragment,
 * exchanges it with our backend, sets the httpOnly session cookie, and routes the user home.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useUserAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await api.post(
          "/auth/google/session",
          {},
          { headers: { "X-Session-ID": sessionId } }
        );
        setUser(data);
        // Strip the hash so a refresh doesn't re-fire this screen
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome, ${data.name?.split(" ")[0] || "friend"}.`);
        navigate(data.role === "admin" ? "/admin/dashboard" : "/", { replace: true });
      } catch (err) {
        toast.error("Could not sign in. Please try again.");
        navigate("/", { replace: true });
      }
    })();
  }, [location.hash, navigate, setUser]);

  return (
    <section className="min-h-screen flex items-center justify-center bg-paper">
      <div className="text-center" data-testid="auth-callback">
        <Loader2 className="animate-spin mx-auto text-palm" size={40} />
        <p className="mt-4 text-ink/70 font-semibold">Weaving you in…</p>
      </div>
    </section>
  );
}
